import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { isNativeApp } from "../components/PayPalCheckout";

// AstroFate currency context.
//
//   • Prices come from GET /api/public/pricing — never hardcoded in the UI
//   • Auto-detect: India (timezone or locale region) → ₹, anywhere else → $
//   • Manual ₹/$ toggle in the navbar, remembered per device
//   • The Android app follows the same country detection as the website; USD
//     checkout there is handed to the browser (PayPal can't run in a WebView)

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STORAGE_KEY = "astrofate_currency";
const GEO_KEY = "astrofate_geo";

const FALLBACK_PLANS = {
  ai_chat: { inr: 51, usd: 1.5 },
  consultation: { inr: 249, usd: 5, inr_old: 501, usd_old: 10 },
  match: { inr: 149, usd: 3 },
};

function looksIndian() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (/^Asia\/(Kolkata|Calcutta)$/i.test(tz)) return true;
    const langs = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
    // en-IN / hi-IN / hi / gu-IN … any Indian locale or region tag
    return langs.some(l => /-IN$/i.test(l) || /^(hi|mr|bn|ta|te|gu|kn|ml|pa|or|as|ur)\b/i.test(l));
  } catch {
    return false;
  }
}

function detectCurrency() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "INR" || stored === "USD") return stored;
    return looksIndian() ? "INR" : "USD";
  } catch {
    return "INR";
  }
}

export const fmtInr = (v) => `₹${Math.round(Number(v) || 0)}`;
export const fmtUsd = (v) => `$${(Number(v) || 0).toFixed(2)}`;
export const fmtPaid = (amount, currency) =>
  (currency || "INR").toUpperCase() === "USD" ? fmtUsd(amount) : fmtInr(amount);

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(detectCurrency);
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [usdEnabled, setUsdEnabled] = useState(true);
  const [rate, setRate] = useState(88);
  const [country, setCountry] = useState("");

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/public/pricing`)
      .then(r => {
        if (!alive || !r.data?.plans) return;
        setPlans(r.data.plans);
        setRate(r.data.usd_inr_rate || 88);
        setUsdEnabled(!!r.data.usd_enabled);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Server-side IP → country. Authoritative over the locale/timezone guess.
  // Cached per device for 6h so it costs one request per session at most.
  useEffect(() => {
    let alive = true;
    const apply = (cc) => {
      if (!alive || !cc) return;
      setCountry(cc);
      if (cc !== "IN") {
        setCurrencyState("USD");
        try { window.localStorage.setItem(STORAGE_KEY, "USD"); } catch { /* private mode */ }
      }
    };
    try {
      const raw = window.localStorage.getItem(GEO_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.country && cached.exp > Date.now()) { apply(cached.country); return () => { alive = false; }; }
      }
    } catch { /* ignore */ }
    axios.get(`${API}/public/geo`)
      .then(r => {
        const cc = (r.data?.country || "").toUpperCase();
        if (!cc) return;
        try { window.localStorage.setItem(GEO_KEY, JSON.stringify({ country: cc, exp: Date.now() + 6 * 3600 * 1000 })); } catch { /* ignore */ }
        apply(cc);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const native = isNativeApp();
  // Outside India there is no ₹ rail — USD/PayPal only, toggle hidden.
  const locked = usdEnabled && !!country && country !== "IN";
  const effective = usdEnabled ? (locked ? "USD" : currency) : "INR";
  const isUSD = effective === "USD";

  const setCurrency = useCallback((next) => {
    if (next !== "INR" && next !== "USD") return;
    if (locked && next === "INR") return;
    setCurrencyState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
  }, [locked]);

  const value = useMemo(() => {
    const entry = (plan) => plans[plan] || FALLBACK_PLANS[plan] || {};
    return {
      currency: effective,
      setCurrency,
      isUSD,
      usdEnabled,
      locked,
      country,
      native,
      rate,
      plans,
      // Formatted current price, e.g. "₹249" / "$5.00"
      price: (plan) => (isUSD ? fmtUsd(entry(plan).usd) : fmtInr(entry(plan).inr)),
      // Formatted pre-discount anchor, or "" when the plan has none
      priceOld: (plan) => {
        const v = isUSD ? entry(plan).usd_old : entry(plan).inr_old;
        return v ? (isUSD ? fmtUsd(v) : fmtInr(v)) : "";
      },
      // Always-INR price (used for the ₹/UPI button even in $ mode)
      priceInr: (plan) => fmtInr(entry(plan).inr),
      priceOldInr: (plan) => (entry(plan).inr_old ? fmtInr(entry(plan).inr_old) : ""),
      amount: (plan) => (isUSD ? entry(plan).usd : entry(plan).inr),
      fmtPaid,
    };
  }, [effective, isUSD, plans, rate, setCurrency, usdEnabled, native, locked, country]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (ctx) return ctx;
  // Defensive default so a component rendered outside the provider still shows ₹ prices.
  return {
    currency: "INR", setCurrency: () => {}, isUSD: false, usdEnabled: false, locked: false, country: "", rate: 88,
    plans: FALLBACK_PLANS,
    price: (p) => fmtInr(FALLBACK_PLANS[p]?.inr),
    priceOld: (p) => (FALLBACK_PLANS[p]?.inr_old ? fmtInr(FALLBACK_PLANS[p].inr_old) : ""),
    priceInr: (p) => fmtInr(FALLBACK_PLANS[p]?.inr),
    priceOldInr: (p) => (FALLBACK_PLANS[p]?.inr_old ? fmtInr(FALLBACK_PLANS[p].inr_old) : ""),
    amount: (p) => FALLBACK_PLANS[p]?.inr,
    fmtPaid,
  };
}
